import { createTheme, alpha } from '@mui/material/styles';

/*
 * Design direction: "security print".
 * Cool paper, deep passport ink, cobalt for actions and a foil-amber accent
 * used sparingly (like the hologram on a real ID). Monospace details echo the
 * machine-readable zone printed on passports and ID cards.
 */
export const tokens = {
  paper: '#F2F4F7',
  paperDeep: '#E6EAF0',
  surface: '#FFFFFF',
  ink: '#0D1B2E',
  inkSoft: '#43536A',
  inkMuted: '#6B7A90',
  line: '#D5DCE5',
  cobalt: '#1F48C4',
  cobaltDeep: '#14318F',
  cobaltTint: '#E3EAFB',
  foil: '#D99A16',
  foilTint: '#FBF0D6',
  night: '#0A1422',
  nightRaise: '#13233A',
  holo: 'linear-gradient(115deg, #7FD6E8 0%, #B9C7FF 30%, #F4B9D3 55%, #F7D58A 80%, #9FE3D0 100%)',
};

export const fonts = {
  sans: "'Schibsted Grotesk', system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: tokens.cobalt, dark: tokens.cobaltDeep, light: '#4C6FDB', contrastText: '#FFFFFF' },
    secondary: { main: tokens.foil, contrastText: tokens.ink },
    background: { default: tokens.paper, paper: tokens.surface },
    text: { primary: tokens.ink, secondary: tokens.inkSoft },
    divider: tokens.line,
  },
  typography: {
    fontFamily: fonts.sans,
    h1: { fontWeight: 700, fontSize: 'clamp(2.35rem, 6vw, 4.1rem)', lineHeight: 1.04, letterSpacing: '-0.035em' },
    h2: { fontWeight: 700, fontSize: 'clamp(1.85rem, 4.2vw, 2.8rem)', lineHeight: 1.1, letterSpacing: '-0.03em' },
    h3: { fontWeight: 700, fontSize: 'clamp(1.5rem, 3vw, 2rem)', lineHeight: 1.15, letterSpacing: '-0.02em' },
    h4: { fontWeight: 650, fontSize: '1.5rem', lineHeight: 1.25, letterSpacing: '-0.015em' },
    h5: { fontWeight: 650, fontSize: '1.2rem', lineHeight: 1.3, letterSpacing: '-0.01em' },
    h6: { fontWeight: 650, fontSize: '1.02rem', lineHeight: 1.4 },
    subtitle1: { fontSize: '1.125rem', lineHeight: 1.6, color: tokens.inkSoft },
    body1: { fontSize: '1rem', lineHeight: 1.7 },
    body2: { fontSize: '0.925rem', lineHeight: 1.65 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    overline: { textTransform: 'none', letterSpacing: '0.02em', fontFamily: fonts.mono, fontSize: '0.78rem', fontWeight: 500 },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: tokens.paper, overflowX: 'hidden' },
        '::selection': { background: alpha(tokens.cobalt, 0.18) },
        ':focus-visible': { outline: `3px solid ${tokens.foil}`, outlineOffset: 3 },
        a: { color: tokens.cobalt },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 999,
          paddingInline: 22,
          minHeight: 46,
          fontSize: '0.98rem',
          '&.Mui-focusVisible': { outline: `3px solid ${tokens.foil}`, outlineOffset: 3 },
        },
        containedPrimary: {
          backgroundColor: tokens.ink,
          '&:hover': { backgroundColor: tokens.cobaltDeep },
        },
        outlined: { borderColor: tokens.line, color: tokens.ink, '&:hover': { borderColor: tokens.ink, backgroundColor: 'transparent' } },
        sizeLarge: { minHeight: 52, paddingInline: 26, fontSize: '1.02rem' },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { '&.Mui-focusVisible': { outline: `3px solid ${tokens.foil}`, outlineOffset: 2 } } },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiCard: {
      styleOverrides: { root: { borderRadius: 20, border: `1px solid ${tokens.line}`, boxShadow: 'none' } },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } } },
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${tokens.line}`,
          borderRadius: 16,
          background: tokens.surface,
          '&:before': { display: 'none' },
          '&:not(:last-of-type)': { marginBottom: 12 },
          '&:first-of-type': { borderRadius: 16 },
          '&:last-of-type': { borderRadius: 16 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { padding: '6px 20px', borderRadius: 16, '&.Mui-focusVisible': { outline: `3px solid ${tokens.foil}`, backgroundColor: 'transparent' } },
        content: { fontWeight: 600 },
      },
    },
    MuiAccordionDetails: { styleOverrides: { root: { padding: '0 20px 20px' } } },
    MuiListItem: { styleOverrides: { root: { paddingTop: 2, paddingBottom: 2 } } },
    MuiDivider: { styleOverrides: { root: { borderColor: tokens.line } } },
    MuiLink: { styleOverrides: { root: { textUnderlineOffset: 3 } } },
  },
});

export default theme;
